// recipe: button · source sha256 df8f03f680e8c315aa54c92130a0dfa724bef01e2b625bb737881afe93e8bdaa · copied 2026-10-24T06:21:36Z · estate-tokens/2
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

/* The act class of a control is its SHAPE (design contract law 5): A filled executes now, B outlined asks first, C dashed hands over and never executes. */

// ---- class A: execute ------------------------------------------------------------------------------------------------------------------------
export interface ButtonProps {
  /** `primary` is the one act the screen is for; every other immediate act is `neutral`. */
  tone?: 'primary' | 'neutral';
  /** Quiet: icon and toolbar acts that are never consequential. */
  ghost?: boolean;
  small?: boolean;
  /** An icon (the `icon` recipe) before the label. */
  icon?: ComponentChildren;
  /** Icon-only: the children become the accessible name and are visually hidden. */
  iconOnly?: boolean;
  busy?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  /** With an href the control is a link (navigation, not an act). */
  href?: string;
  onClick?: (e: MouseEvent) => void;
  title?: string;
  children?: ComponentChildren;
}

/** Class A: filled, immediate. A link when it has an href, a button otherwise. */
export function Button({ tone = 'neutral', ghost, small, icon, iconOnly, busy, disabled, type = 'button', href, onClick, title, children }: ButtonProps) {
  const body = (
    <>
      {busy ? <span class="est-button-spinner" aria-hidden="true" /> : icon}
      {iconOnly ? <span class="est-button-sr">{children}</span> : children}
    </>
  );
  const shared = { 'data-act': 'a', 'data-tone': tone, 'data-ghost': ghost ? '' : undefined, 'data-size': small ? 'sm' : undefined, 'data-icon-only': iconOnly ? '' : undefined, title };
  if (href !== undefined && !disabled) {
    return (
      <a class="est-button" href={href} {...shared}>
        {body}
      </a>
    );
  }
  return (
    <button class="est-button" type={type} disabled={disabled || busy} aria-busy={busy ? 'true' : undefined} onClick={onClick} {...shared}>
      {body}
    </button>
  );
}

// ---- class B: offer (a 550 ms hold, or two steps) ------------------------------------------------------------------------------------------------
/** The class-B hold in ms where no stylesheet is there to ask (a test DOM, an unstyled page): the contract's number (law 5). The hold the control USES is the document's `--est-motion-hold`, read through `holdMs` below. */
export const HOLD_MS = 550;
export const ARM_WINDOW_MS = 4000;

export type HoldPhase = 'idle' | 'holding' | 'armed' | 'done';
export type HoldTrigger = 'pointerDown' | 'holdElapsed' | 'pointerRelease' | 'keyActivate' | 'tap' | 'confirm' | 'disarm' | 'reset';

/** The machine as data (frontend FSM rule A1): the control renders it and owns nothing. A keyboard or assistive-tech activation, and a tap under reduced motion, are ALWAYS the two-step path (decision G-6). */
export const TRANSITIONS: ReadonlyArray<{ from: HoldPhase; to: HoldPhase; trigger: HoldTrigger }> = [
  { from: 'idle', to: 'holding', trigger: 'pointerDown' },
  { from: 'holding', to: 'done', trigger: 'holdElapsed' },
  { from: 'holding', to: 'idle', trigger: 'pointerRelease' },
  { from: 'idle', to: 'armed', trigger: 'keyActivate' },
  { from: 'idle', to: 'armed', trigger: 'tap' },
  { from: 'armed', to: 'done', trigger: 'confirm' },
  { from: 'armed', to: 'idle', trigger: 'disarm' },
  { from: 'done', to: 'idle', trigger: 'reset' },
];

/** The refusal-aware step: null means the table has no such row and the control ignores the trigger. */
export function nextHold(from: HoldPhase, trigger: HoldTrigger): HoldPhase | null {
  const row = TRANSITIONS.find((t) => t.from === from && t.trigger === trigger);
  return row ? row.to : null;
}

const reducedMotion = (): boolean => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The hold in ms, from the stylesheet: recipe.css declares the local knob `--button-hold` as the document's `--est-motion-hold`, and the progress fill's transition reads the same knob,
 * so the timer and the fill agree and the number lives once, in the document. HOLD_MS answers only when the knob cannot be read (no stylesheet, or a value that is not a time).
 */
export function holdMs(el: Element): number {
  if (typeof getComputedStyle !== 'function') return HOLD_MS;
  const raw = getComputedStyle(el).getPropertyValue('--button-hold').trim();
  const n = parseFloat(raw);
  if (!Number.isFinite(n) || n <= 0) return HOLD_MS;
  if (raw.endsWith('ms')) return n;
  return raw.endsWith('s') ? n * 1000 : HOLD_MS;
}

export interface HoldButtonProps {
  /** What it says at rest, e.g. "Mark as paid". */
  label: string;
  /** What it says once armed, e.g. "Press again to confirm". */
  armedLabel?: string;
  tone?: 'neutral' | 'accent' | 'danger';
  icon?: ComponentChildren;
  disabled?: boolean;
  /** Called once, after a completed hold or a confirmed second step. What the act MEANS is the caller's; who decided is recorded by the server from the session, never sent from here. */
  onConfirm: () => void;
  /** Taught after a pointer click that was too short. */
  hint?: string;
  title?: string;
}

/**
 * Class B. A pointer PRESS-AND-HOLD for the document's hold (550 ms, `--est-motion-hold`) with progress; release or leave before it completes and nothing happens. A keyboard activation, a screen reader's click and
 * any tap under reduced motion take the non-timed path: the first activation arms the button, the second one within the window confirms. All state read at event time is in refs.
 */
export function HoldButton({ label, armedLabel = 'Press again to confirm', tone = 'neutral', icon, disabled, onConfirm, hint = 'Press and hold to confirm', title }: HoldButtonProps) {
  const [phase, setPhase] = useState<HoldPhase>('idle');
  const [taught, setTaught] = useState(false);
  const phaseRef = useRef<HoldPhase>('idle');
  const holdTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const armTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const confirmRef = useRef(onConfirm);
  confirmRef.current = onConfirm;

  const clear = () => {
    clearTimeout(holdTimer.current);
    clearTimeout(armTimer.current);
  };
  useEffect(() => clear, []);

  const step = (trigger: HoldTrigger): HoldPhase | null => {
    const to = nextHold(phaseRef.current, trigger);
    if (to === null) return null;
    phaseRef.current = to;
    setPhase(to);
    return to;
  };
  const fire = () => {
    clear();
    confirmRef.current();
    step('reset');
  };
  const arm = (trigger: 'keyActivate' | 'tap') => {
    if (step(trigger) === 'armed') {
      clearTimeout(armTimer.current);
      armTimer.current = setTimeout(() => step('disarm'), ARM_WINDOW_MS);
    }
  };

  const onPointerDown = (e: JSX.TargetedPointerEvent<HTMLButtonElement>) => {
    if (disabled || e.button !== 0) return;
    setTaught(false);
    if (phaseRef.current === 'armed') {
      if (step('confirm') === 'done') fire();
      return;
    }
    if (reducedMotion()) {
      arm('tap');
      return;
    }
    if (step('pointerDown') === 'holding') {
      clearTimeout(holdTimer.current);
      holdTimer.current = setTimeout(() => {
        if (step('holdElapsed') === 'done') fire();
      }, holdMs(e.currentTarget));
    }
  };
  const cancelHold = () => {
    if (phaseRef.current === 'holding') {
      clearTimeout(holdTimer.current);
      step('pointerRelease');
    }
  };
  const onClick = (e: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.detail > 0) {
      // a pointer click: the hold decided (or, too short, only teaches the gesture); under reduced motion pointerdown already armed it
      if (!reducedMotion() && phaseRef.current === 'idle') setTaught(true);
      return;
    }
    // keyboard or assistive technology: the two-step path
    if (phaseRef.current === 'armed') {
      if (step('confirm') === 'done') fire();
    } else arm('keyActivate');
  };
  const onKeyDown = (e: JSX.TargetedKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Escape' && phaseRef.current === 'armed') {
      clearTimeout(armTimer.current);
      step('disarm');
    }
  };
  const onBlur = () => {
    if (phaseRef.current === 'armed') {
      clearTimeout(armTimer.current);
      step('disarm');
    }
  };

  const armed = phase === 'armed';
  return (
    <span class="est-button-wrap">
      <button
        type="button"
        class="est-button"
        data-act="b"
        data-tone={tone}
        data-phase={phase}
        disabled={disabled}
        title={title}
        onPointerDown={onPointerDown}
        onPointerUp={cancelHold}
        onPointerLeave={cancelHold}
        onPointerCancel={cancelHold}
        onContextMenu={(e) => e.preventDefault()}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
      >
        <span class="est-button-fill" aria-hidden="true" />
        {icon}
        {armed ? armedLabel : label}
      </button>
      <span class="est-button-sr" role="status" aria-live="polite">
        {armed ? armedLabel : taught ? hint : ''}
      </span>
      {taught && !armed ? (
        <span class="est-button-hint" aria-hidden="true">
          {hint}
        </span>
      ) : null}
    </span>
  );
}

// ---- class C: hand over -----------------------------------------------------------------------------------------------------------------------------
export interface HandOffProps {
  title: string;
  /** What a person must do elsewhere. */
  children?: ComponentChildren;
  /** Where the runbook for it lives. The only control in a hand-off is this link: a class C act never has a button that acts. */
  runbookHref: string;
  runbookLabel?: string;
  /** How anyone can see that it was done: the effect check. */
  effectCheck: string;
  icon?: ComponentChildren;
}

/** Class C: a dashed hand-off card with its runbook and its effect check. It never executes anything. */
export function HandOff({ title, children, runbookHref, runbookLabel = 'Open the runbook', effectCheck, icon }: HandOffProps) {
  return (
    <aside class="est-handoff" data-act="c" aria-label={title}>
      <h3 class="est-handoff-title">
        {icon}
        {title}
      </h3>
      {children ? <p class="est-handoff-body">{children}</p> : null}
      <p class="est-handoff-effect">
        <strong>Effect check:</strong> {effectCheck}
      </p>
      <div>
        <a class="est-button" data-act="c" href={runbookHref}>
          {runbookLabel}
        </a>
      </div>
    </aside>
  );
}
